<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { Plus, BarChart3 } from 'lucide-vue-next'
import { gradebookApi, assignmentsApi } from '@/api'
import {
  AppButton,
  EmptyState,
  GradePill,
  gradeBand,
  mix,
  paletteFromColour,
  Palette,
  type PaletteColour,
  type PaletteHue,
} from '@/design-system/components'
import type {
  Gradebook,
  GradebookAssignment,
  GradebookStudent,
  GradebookSubmission,
  SubmissionComment,
} from '@/api'
import Monogram from '@/components/classes/Monogram.vue'
import CreateAssignmentModal from '@/components/modals/CreateAssignmentModal.vue'
import GradeEntryModal from '@/components/modals/GradeEntryModal.vue'
import GradeViewModal from '@/components/modals/GradeViewModal.vue'

/**
 * GradesTab — full-width gradebook. Sticky flexing student column + horizontally
 * scrolling assignment columns inside the card. Scores coloured by band on a
 * faint class-tinted scaffold. Cells open the grade entry modal.
 */
const props = defineProps<{ classId: string; hue: PaletteHue; gradebook: Gradebook | null }>()
const emit = defineEmits<{ changed: [] }>()

const router = useRouter()
const SW = 220
const CW = 96
const FW = 84
const headBand = computed(() => mix(props.hue.solid, 0.1))
const colBorder = computed(() => mix(props.hue.solid, 0.18))
const zebra = computed(() => mix(props.hue.solid, 0.025))

const assignments = computed(() => props.gradebook?.assignments ?? [])
const students = computed(() => props.gradebook?.students ?? [])
const minWidth = computed(() => SW + assignments.value.length * CW + FW)

// (studentId|assignmentId) → submission
const subMap = computed(() => {
  const m = new Map<string, GradebookSubmission>()
  for (const s of props.gradebook?.submissions ?? []) {
    m.set(`${s.studentId}|${s.assignment.id}`, s)
  }
  return m
})

const finalById = computed(() => {
  const m: Record<string, number | null> = {}
  for (const sg of props.gradebook?.studentGrades ?? []) {
    m[sg.studentId] = sg.finalGrade?.percentage ?? null
  }
  return m
})

function categoryHue(colour?: string): PaletteHue {
  return colour ? paletteFromColour(colour as PaletteColour) : Palette.grey
}

interface Cell {
  text: string
  ink: string
  graded: boolean
}
function cellFor(studentId: string, a: GradebookAssignment): Cell {
  const sub = subMap.value.get(`${studentId}|${a.id}`)
  if (!sub?.marking) return { text: '—', ink: 'var(--ink-300)', graded: false }
  const m = sub.marking
  const pct = sub.grade != null ? Math.round(sub.grade * 100) : (m.percent ?? null)
  // Marking-method-aware: a scale grade always shows its letter, never the underlying score.
  let text: string
  switch (m.type) {
    case 'SCALE':
      text = m.level?.text ?? '—'
      break
    case 'PERCENT':
      text = m.percent != null ? `${Math.round(m.percent)}%` : '—'
      break
    default: // INTEGER / DECIMAL — max is shown in the column header
      text = m.score != null ? String(m.score) : '—'
  }
  return { text, ink: gradeBand(pct).ink, graded: true }
}

// The full /gradebook payload has no comment info; the lightweight /gradebook/marks
// endpoint carries the per-cell `hasComment` flag (same source Android uses).
const commentKeys = ref<Set<string>>(new Set())
async function loadMarks() {
  try {
    const marks = await gradebookApi.getGradebookMarks(props.classId)
    commentKeys.value = new Set(
      marks.filter((m) => m.hasComment).map((m) => `${m.studentId}|${m.assignmentId}`),
    )
  } catch {
    commentKeys.value = new Set()
  }
}
watch(() => [props.classId, props.gradebook], loadMarks, { immediate: true })
function hasComment(studentId: string, assignmentId: string): boolean {
  return commentKeys.value.has(`${studentId}|${assignmentId}`)
}

// ── Modals ───────────────────────────────────────────────────────────────────
const showCreate = ref(false)
const showView = ref(false)
const showEntry = ref(false)
const selStudent = ref<GradebookStudent | null>(null)
const selAssignment = ref<GradebookAssignment | null>(null)
const selSubmission = ref<GradebookSubmission | null>(null)

async function openCell(s: GradebookStudent, a: GradebookAssignment) {
  selStudent.value = s
  selAssignment.value = a
  const sub = subMap.value.get(`${s.id}|${a.id}`) ?? null
  if (sub?.marking) {
    // Existing grade → open the read-only view first (edit is a button inside it).
    // The gradebook payload omits comment bodies, so hydrate it from the
    // per-assignment endpoint when the cell flags a comment.
    let comments = sub.comments ?? []
    if (!comments.length && hasComment(s.id, a.id)) {
      try {
        const details = await assignmentsApi.getAssignmentDetails(a.id)
        const body = details.students?.find((x) => x.student.id === s.id)?.grade?.latestComment
        if (body) comments = [{ id: '', body, createdAt: '' } as SubmissionComment]
      } catch {
        /* fall back to no comment body */
      }
    }
    selSubmission.value = { ...sub, comments }
    showView.value = true
  } else {
    // No grade yet → straight to entry.
    selSubmission.value = sub
    showEntry.value = true
  }
}
function onEdit() {
  showView.value = false
  showEntry.value = true
}
function onChanged() {
  emit('changed')
}
</script>

<template>
  <div>
    <div class="gb__toolbar">
      <span class="gb__eyebrow">Gradebook · {{ assignments.length }} assignments</span>
      <AppButton variant="secondary" size="sm" @click="showCreate = true">
        <Plus :size="14" /> New assignment
      </AppButton>
    </div>

    <EmptyState
      v-if="!assignments.length"
      :icon="BarChart3"
      :hue="hue"
      :cta-icon="Plus"
      title="No assignments yet"
      body="Create an assignment to start marking and building this class's gradebook."
      cta="New assignment"
      @action="showCreate = true"
    />
    <div v-else class="gb__card">
      <div class="gb__scroll">
        <div class="gb__inner" :style="{ minWidth: minWidth + 'px' }">
          <!-- header -->
          <div class="gb__hrow" :style="{ background: headBand }">
            <div class="gb__hstudent" :style="{ width: SW + 'px', background: headBand }">Student</div>
            <button
              v-for="a in assignments"
              :key="a.id"
              type="button"
              class="gb__hcol"
              :style="{ width: CW + 'px', borderLeft: `1px solid ${colBorder}` }"
              @click="router.push(`/classes/${classId}/assignment/${a.id}`)"
            >
              <span
                class="gb__chip"
                :style="{ background: categoryHue(a.category?.colour).soft, color: categoryHue(a.category?.colour).ink }"
              >
                <span class="gb__chip-dot" :style="{ background: categoryHue(a.category?.colour).solid }" />
                {{ a.category?.name ?? 'Grade' }}
              </span>
              <div class="gb__atitle">{{ a.assessmentName }}</div>
              <div class="gb__amax">{{ a.markingMethod.type === 'SCALE' ? 'Scale' : a.markingMethod.type === 'PERCENT' ? '%' : `/ ${a.markingMethod.maxPoints ?? '—'}` }}</div>
            </button>
            <div
              class="gb__hfinal"
              :style="{ width: FW + 'px', borderLeft: `1px solid ${colBorder}` }"
            >
              Final
            </div>
          </div>

          <!-- rows -->
          <div
            v-for="(s, ri) in students"
            :key="s.id"
            class="gb__row"
            :style="{ background: ri % 2 ? zebra : 'var(--surface)' }"
          >
            <div class="gb__student" :style="{ width: SW + 'px' }">
              <Monogram :first="s.firstName" :last="s.lastName" :key-id="s.id" :photo-url="s.photoUrl" :size="30" />
              <span class="gb__sname"><b>{{ s.lastName }}</b>, {{ s.firstName }}</span>
            </div>
            <button
              v-for="a in assignments"
              :key="a.id"
              type="button"
              class="gb__cell"
              :style="{ width: CW + 'px' }"
              @click="openCell(s, a)"
            >
              <span class="gb__score" :style="{ color: cellFor(s.id, a).ink }">{{ cellFor(s.id, a).text }}</span>
              <span v-if="hasComment(s.id, a.id)" class="gb__cmt" aria-label="Has comment" />
            </button>
            <div class="gb__final" :style="{ width: FW + 'px' }">
              <GradePill :pct="finalById[s.id] ?? null" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="assignments.length" class="gb__foot">Showing {{ students.length }} students · scroll columns horizontally</div>

    <CreateAssignmentModal v-model="showCreate" :class-id="classId" @created="onChanged" />
    <GradeViewModal
      v-model="showView"
      :student="selStudent"
      :submission="selSubmission"
      :assignment-name="selAssignment?.assessmentName"
      @edit="onEdit"
      @deleted="onChanged"
    />
    <GradeEntryModal
      v-model="showEntry"
      :student="selStudent"
      :assignment="selAssignment"
      :submission="selSubmission"
      :scales="gradebook?.scales ?? []"
      source="gradebook"
      @saved="onChanged"
      @deleted="onChanged"
    />
  </div>
</template>

<style scoped>
.gb__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.gb__eyebrow {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: var(--ink-400);
}
.gb__card {
  border-radius: var(--radius-lg);
  box-shadow: var(--hairline-border);
  overflow: hidden;
  background: var(--surface);
}
.gb__scroll {
  overflow-x: auto;
}
.gb__inner {
  font-family: var(--font-sans);
}
.gb__hrow {
  display: flex;
  border-bottom: 1px solid var(--outline-variant);
}
.gb__hstudent {
  flex-shrink: 0;
  padding: 12px 16px;
  position: sticky;
  left: 0;
  z-index: 2;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: var(--ink-500);
}
.gb__hcol {
  flex-shrink: 0;
  padding: 10px;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
  font-family: var(--font-sans);
  transition: background var(--motion-fast);
}
.gb__hcol:hover {
  background: rgba(255, 255, 255, 0.5);
}
.gb__chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 7px 2px 5px;
  border-radius: var(--radius-sm);
  font-size: 9.5px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}
.gb__chip-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
}
.gb__atitle {
  margin-top: 5px;
  font-size: 11.5px;
  font-weight: 500;
  line-height: 1.25;
  color: var(--ink-700);
}
.gb__amax {
  margin-top: 3px;
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-size: 10.5px;
  color: var(--ink-400);
}
.gb__hfinal {
  flex-shrink: 0;
  padding: 12px;
  text-align: right;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: var(--ink-500);
}
.gb__row {
  display: flex;
  border-top: 1px solid var(--hairline);
}
.gb__student {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 9px 16px;
  position: sticky;
  left: 0;
  z-index: 1;
  background: inherit;
}
.gb__sname {
  font-size: 13.5px;
  color: var(--ink-600);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.gb__sname b {
  font-weight: 600;
  color: var(--text-primary);
}
.gb__cell {
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 9px 8px;
  border: 0;
  border-left: 1px solid var(--hairline);
  background: transparent;
  cursor: pointer;
}
.gb__cmt {
  position: absolute;
  top: 5px;
  right: 5px;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--palette-indigo-ink);
}
.gb__cell:hover {
  background: var(--ink-50);
}
.gb__score {
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-size: 13.5px;
  font-weight: 600;
}
.gb__final {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 9px 12px;
  border-left: 1px solid var(--hairline);
}
.gb__foot {
  margin-top: 10px;
  text-align: center;
  font-size: 12.5px;
  color: var(--ink-500);
}
</style>
